import {
  Button,
  ButtonDropdown,
  SidebarCore,
  SkeletonLoader,
  type TDropdownItem,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';

import meKeys from '@/api/queries/me/keyFactory';
import useGetChatAgentConversations from '@/api/queries/me/useGetChatAgentConversations';
import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import { useChatAgent } from '@/ui/layouts/AuthenticatedRootLayout';

import useGetMe from '@/api/queries/me/useGetMe';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { type PageContext, useChatStream } from './useChatStream';

const ChatAgent: FC<{ isOpen: boolean }> = (props) => {
  const { isOpen } = props;
  const queryClient = useQueryClient();
  const { user } = useGetMe();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const isMdAndUp = useIsContainerBeyondBreakpoint('md', {
    container: 'document',
  });
  // Track whether to auto-scroll as streaming content arrives
  const shouldStickToBottom = useRef(true);
  const {
    conversationId: activeConversationId,
    messages,
    isLoading,
    isStreaming,
    error,
    isDisabled,
    status,
    streamingConversationIds,
    sendMessage,
    switchConversation,
  } = useChatStream({
    onMutationComplete: () => {
      void queryClient.invalidateQueries();
    },
    onNewChatConfirmation: () => {
      void queryClient.invalidateQueries({
        queryKey: meKeys.chatAgentConversations(),
      });
      inputRef.current?.focus();
    },
    onStreamComplete: () => {
      void queryClient.invalidateQueries({
        queryKey: meKeys.chatAgentConversations(),
      });
      inputRef.current?.focus();
    },
  });
  const { hideChatAgent } = useChatAgent();
  const location = useLocation();
  const { chatAgentConversations, fetchNextPage, hasNextPage } =
    useGetChatAgentConversations();

  // Detect when user manually scrolls away from bottom
  // With flex-col-reverse, scrollTop near 0 = at bottom
  const handleScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const isAtBottom = el.scrollTop <= 50;
    shouldStickToBottom.current = isAtBottom;
  }, []);

  // Reset sticky scroll when switching conversations
  useEffect(() => {
    shouldStickToBottom.current = true;
  }, [activeConversationId]);

  // Auto-scroll to bottom when messages update during streaming
  useEffect(() => {
    if (isStreaming && shouldStickToBottom.current) {
      bodyRef.current?.scrollTo({ top: 0 });
    }
  }, [messages, isStreaming]);

  // Collect page context and send with message
  // Note: useRouteMatch()/useParams() are not used here because ChatAgent
  // is mounted above the route definitions (in AuthenticatedRootLayout which wraps
  // <Route path="/" />), so they would always return the root "/" match.
  // useLocation().pathname gives the correct current path.
  const handleSendMessage = useCallback(
    (message: string) => {
      const context: PageContext = {
        pathname: location.pathname,
        searchParams: Object.fromEntries(new URLSearchParams(location.search)),
        mountedQueryKeys: queryClient
          .getQueryCache()
          .getAll()
          .filter((q) => q.state.status === 'success')
          .map((q) => q.queryKey as unknown[]),
      };
      void sendMessage(message, context);
      // Re-enable sticky scroll and jump to bottom
      shouldStickToBottom.current = true;
      bodyRef.current?.scrollTo({ top: 0 });
    },
    [location.pathname, location.search, queryClient, sendMessage]
  );

  // Todo: title from the useChatStream hook does not update until you switch conversations - fix that
  const currentConversationTitle = chatAgentConversations?.find(
    (i) => i.id === activeConversationId
  )?.title;

  const conversationDropdownItems = useMemo<TDropdownItem[]>(() => {
    const newChatItem: TDropdownItem = {
      label: '+ New chat',
      searchKey: 'New chat',
      selected: activeConversationId === null,
      onClick: () => {
        switchConversation(null);
        inputRef.current?.focus();
      },
    };

    const conversationItems: TDropdownItem[] =
      chatAgentConversations?.map((conversation) => ({
        label: conversation.title || 'Untitled Conversation',
        searchKey: conversation.title || 'Untitled Conversation',
        selected: conversation.id === activeConversationId,
        isProcessing: streamingConversationIds.has(conversation.id),
        onClick: () => {
          switchConversation(conversation.id);
          inputRef.current?.focus();
        },
      })) ?? [];

    return [newChatItem, ...conversationItems];
  }, [
    activeConversationId,
    chatAgentConversations,
    streamingConversationIds,
    switchConversation,
  ]);

  const panelInner: ReactNode = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b shrink-0">
        <ButtonDropdown
          variant="ghost"
          size="xs"
          colour="black"
          dropdownOptions={{
            items: conversationDropdownItems,
            hasSearch: true,
            anchor: 'start',
            footer: hasNextPage ? (
              <button
                type="button"
                className="w-full p-2 text-sm text-gray-500 hover:bg-gray-50"
                onClick={() => void fetchNextPage()}
              >
                Load more...
              </button>
            ) : undefined,
          }}
        >
          {currentConversationTitle || 'New chat'}
          <UntitledIcon name="chevron-selector-vertical" className="size-4" />
        </ButtonDropdown>
        <Button
          onClick={hideChatAgent}
          variant="ghost"
          size="sm"
          colour="black"
        >
          <span className="sr-only">Close chat panel</span>
          <UntitledIcon
            name={isMdAndUp ? 'chevron-right-double.3' : 'x-close'}
            className="size-5"
          />
        </Button>
      </div>

      {/* Messages area */}
      <AnimatePresence mode="wait">
        <motion.div
          ref={bodyRef}
          key={activeConversationId}
          className="flex-1 flex flex-col-reverse gap-4 p-4 overflow-y-auto min-w-0 typography:prose"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onScroll={handleScroll}
        >
          {isLoading && (
            <SkeletonLoader.motion
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="h-10 w-full"
            />
          )}

          {messages.length === 0 && !isLoading && (
            <div className="text-center m-auto typography:prose max-w-72 space-y-1.5">
              <h3>Hi {user?.firstName}, I'm Agent Dave 👋</h3>
              <p className="text-gray-500">Ask me anything</p>
              <p className="text-gray-500">
                I have access to Payaca's help docs and can get a lot done for
                you
              </p>
              <p className="text-gray-500">
                If you need a human, click the{' '}
                <UntitledIcon
                  className="size-5 align-middle"
                  name="help-circle.3"
                />{' '}
                below at any point
              </p>
            </div>
          )}
          {messages.toReversed().map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          {error && (
            <div className="rounded bg-red-100 p-2 text-sm text-red-600">
              {error}
            </div>
          )}
          {status === 'compacting' && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              Conversation limit reached. Compacting so we can continue...
            </div>
          )}
          {status === 'retrying' && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              Something went wrong. Retrying...
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="p-4 pt-0 shrink-0">
        <ChatInput
          ref={inputRef}
          onSend={handleSendMessage}
          disabled={isStreaming || isDisabled}
          messages={messages}
        />
      </div>
    </>
  );

  if (!isMdAndUp) {
    return (
      <SidebarCore isOpen={isOpen} onClose={hideChatAgent} size="standard">
        {panelInner}
      </SidebarCore>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="chat-panel"
          initial={{ width: 0 }}
          animate={{ width: 'max(350px, 25vw)' }}
          exit={{ width: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="typography:prose h-full overflow-hidden shrink-0 grid grid-rows-1"
        >
          <aside
            aria-label="Chat with Dave"
            className="flex flex-col min-w-0 min-h-0 bg-white border m-2 ml-0 rounded-lg h-auto"
            style={{ width: 'calc(max(350px, 25vw) - 8px)' }}
          >
            {panelInner}
          </aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChatAgent;
